import { useTranslation } from 'react-i18next';
import { formatSiteDateTime } from '@d-orca/shared/client/utils';
import type { ITaskLogEntry } from '@interfaces/IScheduler';
import { describeLogEntry, LOG_KIND_DOT } from '../../lib/logDisplay';
import styles from './TaskLogSection.module.css';

// ---------------------------------------------------------------------------
// TaskLogSection — what has happened to this task, newest first.
//
// Called the LOG, never "activity": an Activity is the execution facet of a
// task (`activities` collection, the Trusstor side of a commit), and a drawer
// section that borrows the word makes two different things share one name.
// This is the task's own audit trail — status flips, progress, area edits,
// checklist, comments, stock reconciliation.
// ---------------------------------------------------------------------------

interface ITaskLogSectionProps {
  /** The task's server-generated log (optimistically mirrored by the store). */
  entries: readonly ITaskLogEntry[];
  /** The site's zone: entries read on the SITE's wall clock — the one stamp
   *  every drawer section shares. Unknown → no time rather than a guessed one. */
  tz: string | undefined;
}

export function TaskLogSection({ entries, tz }: ITaskLogSectionProps) {
  const { t, i18n } = useTranslation();

  return (
    <section className={styles.section}>
      <h3 className={styles.title}>{t('scheduler.panel.log.title')}</h3>
      {entries.length === 0
        ? <p className={styles.empty}>{t('scheduler.panel.log.empty')}</p>
        : (
          <ul className={styles.list}>
            {/* The log is server-generated + structured; render each kind
                client-side via i18n so the language follows the viewer. */}
            {[...entries].sort((a, b) => (a.at < b.at ? 1 : -1)).map(item => (
              <li key={item.id} className={styles.item}>
                <span className={`${styles.dot} ${styles[LOG_KIND_DOT[item.kind]]}`} aria-hidden />
                <div className={styles.body}>
                  <p className={styles.text}>{describeLogEntry(t, item)}</p>
                  <time className={styles.time} dateTime={item.at}>{tz ? formatSiteDateTime(item.at, tz, i18n.language) : ''}</time>
                </div>
              </li>
            ))}
          </ul>
        )}
    </section>
  );
}
